import { Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface PendingAction {
  id: string;
  type: string;
  description: string;
  params: Record<string, unknown>;
  status: 'pending' | 'confirmed' | 'cancelled' | 'executing';
}

interface ActionConfirmationProps {
  action: PendingAction;
  onConfirm: (actionId: string) => void;
  onCancel: (actionId: string) => void;
}

export function ActionConfirmation({ action, onConfirm, onCancel }: ActionConfirmationProps) {
  const isPending = action.status === 'pending';
  const isExecuting = action.status === 'executing';
  const isConfirmed = action.status === 'confirmed';
  const isCancelled = action.status === 'cancelled';

  return (
    <div className={cn(
      "mt-3 p-3 rounded-xl border transition-all",
      isPending && "border-primary/30 bg-primary/5",
      isExecuting && "border-amber-500/30 bg-amber-500/5",
      isConfirmed && "border-emerald-500/30 bg-emerald-500/5",
      isCancelled && "border-muted bg-muted/30"
    )}>
      <div className="flex items-start gap-3">
        <div className={cn(
          "shrink-0 w-8 h-8 rounded-lg flex items-center justify-center",
          isPending && "bg-primary/10 text-primary",
          isExecuting && "bg-amber-500/10 text-amber-600",
          isConfirmed && "bg-emerald-500/10 text-emerald-600",
          isCancelled && "bg-muted text-muted-foreground"
        )}>
          {isExecuting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isConfirmed ? (
            <Check className="h-4 w-4" />
          ) : isCancelled ? (
            <X className="h-4 w-4" />
          ) : (
            <span className="text-xs font-bold">!</span>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {getActionTitle(action.type)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {action.description}
          </p>
          
          {isPending && (
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={() => onConfirm(action.id)}
                className="h-8 px-4 gap-1.5"
              >
                <Check className="h-3.5 w-3.5" />
                Proceed
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onCancel(action.id)}
                className="h-8 px-4 gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </Button>
            </div>
          )}
          
          {isExecuting && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              Executing action...
            </p>
          )}
          
          {isConfirmed && (
            <p className="text-xs text-emerald-600 mt-2">
              ✓ Action completed successfully
            </p>
          )}
          
          {isCancelled && (
            <p className="text-xs text-muted-foreground mt-2">
              Action cancelled
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function getActionTitle(type: string): string {
  const titles: Record<string, string> = {
    add_supplier_from_marketplace: 'Add Supplier',
    request_new_supplier: 'Request New Supplier',
    create_poam: 'Create Action Action',
    assign_poam: 'Assign Action',
  };
  return titles[type] || 'Confirm Action';
}
